import { useCallback, useEffect, useState } from "react";

const standaloneNow = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true);

export default function useAdaptiveCompanionInstall() {
  const [installPrompt, setInstallPrompt] = useState(() =>
    typeof window !== "undefined"
      ? window.__adaptiveCompanionInstallPrompt || null
      : null,
  );
  const [installed, setInstalled] = useState(standaloneNow);
  const [manualHelp, setManualHelp] = useState(false);

  useEffect(() => {
    const ready = () => {
      setInstallPrompt(window.__adaptiveCompanionInstallPrompt || null);
      setManualHelp(false);
    };
    const complete = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setManualHelp(false);
    };
    const displayMode = window.matchMedia?.("(display-mode: standalone)");
    const displayChange = () => setInstalled(standaloneNow());

    window.addEventListener("adaptive-companion-install-ready", ready);
    window.addEventListener("adaptive-companion-installed", complete);
    displayMode?.addEventListener?.("change", displayChange);

    return () => {
      window.removeEventListener("adaptive-companion-install-ready", ready);
      window.removeEventListener("adaptive-companion-installed", complete);
      displayMode?.removeEventListener?.("change", displayChange);
    };
  }, []);

  const install = useCallback(async () => {
    if (installed || standaloneNow()) {
      setInstalled(true);
      return { installed: true, outcome: "already-installed" };
    }
    if (!installPrompt) {
      setManualHelp(true);
      return { installed: false, outcome: "manual-help" };
    }

    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      const accepted = choice?.outcome === "accepted";
      setInstalled(accepted || standaloneNow());
      setInstallPrompt(null);
      setManualHelp(!accepted);
      return { installed: accepted, outcome: choice?.outcome || "dismissed" };
    } catch (_error) {
      setManualHelp(true);
      return { installed: false, outcome: "error" };
    }
  }, [installPrompt, installed]);

  return {
    installed,
    installPrompt,
    canPrompt: Boolean(installPrompt),
    manualHelp,
    setManualHelp,
    install,
  };
}
